import { Component, createRef, RefObject } from 'react';
import { ILobbyState } from '../../../Core/State/ILobbyState';
import { IRoomState } from '../../../Core/State/IRoomState';
import { IUserState } from '../../../Core/State/IUserState';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import RoomInformation from '../Common/RoomInformation';
import styles from '../../../assets/css/components/Lobby.module.css';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { createTheme, ThemeProvider, Tooltip, Box } from '@mui/material';
import { t } from '../../../i18n';
import tableImageLow from '/images/table/desktop-game-table-low.svg';
import tableImageMid from '/images/table/desktop-game-table-mid.svg';
import tableImageHigh from '/images/table/desktop-game-table-high.svg';
import playersIcon from '/images/lobby/players.svg';
import bnbIcon from '/crypto/bnb.svg';
import busdIcon from '/crypto/busd.svg';
import daiIcon from '/crypto/dai.svg';
import dogeIcon from '/crypto/doge.svg';
import dotIcon from '/crypto/dot.svg';
import shibIcon from '/crypto/shib.svg';
import solIcon from '/crypto/sol.svg';
import solamaIcon from '/crypto/solama.svg';
import usdcIcon from '/crypto/usdc.svg';
import usdtIcon from '/crypto/usdt.svg';
import vlxIcon from '/crypto/vlx.svg';
import xmrIcon from '/crypto/xmr.svg';

interface LobbyProps {
    joinRoom: (roomId: string) => void;
    joinedRoomsState: any[];
    userState: IUserState;
    lobbyState: ILobbyState;
    selectedRoomId: string | null;
    sendChatMessage: (message: string) => void;
}

interface LobbyState {
    chatInput: string;
    selectedRoom: IRoomState | null;
    isDragging: boolean;
    lastClientY: number;
}

export default class DesktopAofLobby extends Component<LobbyProps, LobbyState> {
    private dataGridRef: RefObject<HTMLDivElement>;
    private lastClientY: number = 0;
    
    constructor(props: LobbyProps) {
        super(props);
        this.state = {
            chatInput: '',
            selectedRoom: null,
            isDragging: false,
            lastClientY: 0
        };
        this.dataGridRef = createRef<HTMLDivElement>();
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    componentDidMount(): void {
        const tableImages = [tableImageLow, tableImageMid, tableImageHigh];
        tableImages.forEach((image: string) => {
            const img = new Image();
            img.src = image;
        });
    }

    componentDidUpdate(prevProps: LobbyProps) {
        if (this.state.selectedRoom && prevProps.lobbyState !== this.props.lobbyState) {
            const updatedRoom = this.props.lobbyState.rooms.find(room => room.roomId === this.state.selectedRoom?.roomId);
            if (updatedRoom) {
                this.setState({ selectedRoom: updatedRoom });
            }
        }

        // Update selected room when selectedRoomId changes
        if (prevProps.selectedRoomId !== this.props.selectedRoomId) {
            const selectedRoom = this.props.lobbyState.rooms.find(room => room.roomId === this.props.selectedRoomId);
            this.setState({ selectedRoom: selectedRoom || null });
        }
    }

    handleRowClick = (params: GridRowParams) => {
        this.setState({ selectedRoom: params.row as IRoomState });
    };

    getCryptoIcon = (currency: string) => {
        switch(currency?.toLowerCase()) {
            case 'bnb':
                return bnbIcon;
            case 'busd':
                return busdIcon;
            case 'dai':
                return daiIcon;
            case 'doge':
                return dogeIcon;
            case 'dot':
                return dotIcon;
            case 'shib':
                return shibIcon;
            case 'sol':
                return solIcon;
            case 'solama':
                return solamaIcon;
            case 'usdc':
                return usdcIcon;
            case 'usdt':
                return usdtIcon;
            case 'vlx':
                return vlxIcon;
            case 'xmr':
                return xmrIcon;
            default:
                return null;
        }
    };

    handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Prevent default to avoid text selection during drag
        e.preventDefault();
        this.lastClientY = e.clientY;
        this.setState({ isDragging: true });
    };

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.isDragging && this.dataGridRef.current) {
            const delta = this.lastClientY - e.clientY;
            
            // Find the virtual scroller element inside the DataGrid
            const virtualScroller = this.dataGridRef.current.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
            if (virtualScroller) {
                virtualScroller.scrollTop += delta;
                this.lastClientY = e.clientY;
            }
        }
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
    };

    handleMouseLeave = () => {
        this.setState({ isDragging: false });
    };

    render() {
        const user = this.props.userState;
        
        const filteredRooms = this.props.lobbyState.rooms.filter(room => 
            room.type === 'aof'
        );

        const columns: GridColDef<IRoomState>[] = [
            {
                field: 'name',
                headerName: t('DesktopAofLobby:columns.table'),
                headerClassName: styles.tableHeader,
                flex: 2,
                type: 'string',
                resizable: false,
                renderCell: (params) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span>{params.value}</span>
                    </div>
                )
            },
            {
                field: 'type',
                headerName: t('DesktopAofLobby:columns.game'), 
                headerClassName: styles.tableHeader, 
                flex: 1, 
                type: 'string', 
                resizable: false, 
                valueGetter: (params: any) => {
                    switch (params.value.toLowerCase()) {
                        case 'texas':
                            return t('DesktopAofLobby:gameTypes.holdem');
                        case 'aof':
                            return t('DesktopAofLobby:gameTypes.aof');
                        default:
                            return params.value.charAt(0).toUpperCase() + params.value.slice(1);
                    }
                },
                renderCell: (params) => {
                    const value = params.value?.toLowerCase();

                    switch (value) {
                        case 'texas':
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                    {params.value}
                                </Box>
                            );
                        case 'aof':
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#57222E', color: '#FC5468' }}>
                                    {params.value}
                                </Box>
                            );
                        default:
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                    {params.value}
                                </Box>
                            );
                    }
                }
            },
            {
                field: 'currency',
                headerName: t('DesktopAofLobby:columns.currency'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                renderCell: (params) => {
                    const icon = this.getCryptoIcon(params.row.currency);
                    return icon ? (
                        <>
                            <img 
                                src={icon} 
                                alt={params.row.currency} 
                                style={{ width: '20px', height: '20px', marginRight: '6px' }}
                            />
                            <span style={{fontSize:'14px'}}>{params.row.currency}</span>
                        </>
                    ) : params.row.currency;
                }
            },
            {
                field: 'stakes',
                headerName: t('DesktopAofLobby:columns.stakes'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false,
                valueGetter: (params: any) => params.row.ante,
                renderCell: (params) => {
                    if (params.row.currencyType === 'fiat') {
                        return `${t('DesktopAofLobby:columns.ante')}: $${params.value}`;
                    }
                    const cryptoIcon = this.getCryptoIcon(params.row.currency);
                    const formatCurrency = (value: number) => {
                        return new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2
                        }).format(value);
                    };
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{`${t('DesktopAofLobby:columns.ante')}: ${formatCurrency(params.row.ante)}`}</span>
                            {cryptoIcon && (
                                <Tooltip title={params.row.currency} placement="top" arrow>
                                    <img 
                                        src={cryptoIcon} 
                                        alt={params.row.currency} 
                                        style={{ width: '16px', height: '16px', marginLeft: '6px' }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                }
            },
            {
                field: 'buyIn',
                headerName: t('DesktopAofLobby:columns.buyIn'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'string',
                resizable: false, 
                valueGetter: (params: any) => params.row.maxBuyIn,
                renderCell: (params: any) => {
                    if (params.row.currencyType === 'fiat') {
                        const formatCurrency = (value: number) => {
                            return new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                                maximumFractionDigits: 2
                            }).format(value);
                        };
                        return formatCurrency(params.value);
                    } else {
                        const cryptoIcon = this.getCryptoIcon(params.row.currency);
                        const formatCurrency = (value: number) => {
                            return new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                                maximumFractionDigits: 2
                            }).format(value);
                        };
                        return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span>{formatCurrency(params.value)}</span>
                                {cryptoIcon && (
                                    <Tooltip title={params.row.currency} placement="top" arrow>
                                        <img 
                                            src={cryptoIcon} 
                                            alt={params.row.currency} 
                                            style={{ width: '16px', height: '16px', marginLeft: '6px' }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    }
                }
            },
            {
                field: 'seats',
                headerName: t('DesktopAofLobby:columns.players'),
                headerClassName: styles.tableHeader,
                flex: 1,
                type: 'number',
                resizable: false,
                valueGetter: (params: any) => params.row.playerSatCount,
                align: 'left',
                headerAlign: 'left',
                renderCell: (params) => {
                    const { playerSatCount, minPlayers, maxPlayers } = params.row;

                    let textColor = '#179F55';
                    let tooltip = t('DesktopAofLobby:tooltips.openSeats');

                    if (playerSatCount < minPlayers) {
                        textColor = '#FFC100';
                        tooltip = t('DesktopAofLobby:tooltips.needsPlayers', { count: minPlayers });
                    } else if (playerSatCount === maxPlayers) {
                        textColor = '#FF004D';
                        tooltip = t('DesktopAofLobby:tooltips.tableFull');
                    }
                
                    return (
                        <Tooltip title={tooltip} placement="top" arrow>
                            <Box className={styles.playerCount} sx={{ '--color': textColor }}>
                                <img src={playersIcon} alt="Players Icon" style={{ width: 15, height: 13, marginRight: 4 }} />
                                {`${Math.min(params.row.players.length, params.row.maxPlayers)} / ${params.row.maxPlayers}`}
                            </Box>
                        </Tooltip>
                    );
                }
            },
            {
                field: 'join',
                headerName: '',
                headerClassName: styles.tableHeader,
                cellClassName: styles.lastTableCell,
                flex: 1.5,
                type: 'actions',
                resizable: false,
                renderCell: (params: any) => (
                    <Button
                        variant="contained"
                        fullWidth
                        className={this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? styles.openedButton : styles.joinButton}
                        onClick={() => {
                            this.props.joinRoom(params.row.roomId);
                        }}
                    >
                        {this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? t('DesktopAofLobby:buttons.tableOpened') : t('DesktopAofLobby:buttons.openTable')}
                    </Button>
                ),
                align: 'left',
                headerAlign: 'left',
            },
        ];

        const customDataGridClasses = {
            virtualScroller: styles.customScroller,
            columnHeaders: styles.tableHeaderRow,
            columnHeader: styles.tableHeaderCell,
            cell: styles.tableCell
        };

        return (
            <ThemeProvider theme={this.darkTheme}>
                <div style={{ display: 'flex', width: '100%', height: 'calc(100dvh - 140px)', gap: '1rem', padding: '1rem 1rem 0 1rem', boxSizing: 'border-box' }}>
                    <div
                        style={{ width: 'calc(100% - 1rem - 256px)', height: '100%', flexGrow: 0, flexShrink: 0, boxSizing: 'border-box' }}
                        ref={this.dataGridRef}
                        onMouseDown={this.handleMouseDown}
                        onMouseMove={this.handleMouseMove}
                        onMouseUp={this.handleMouseUp}
                        onMouseLeave={this.handleMouseLeave}
                    >
                        <DataGrid
                            disableColumnMenu
                            disableColumnFilter
                            getCellClassName={() => styles.tableCell}
                            getRowClassName={(params) => {
                                const userId = user?.id;
                                if (!userId) {
                                    return styles.tableRow;
                                }
                                const isUserPlaying = params.row.players.some((player: { userId: string }) => player.userId === user.id);
                                return isUserPlaying ? styles.active : styles.tableRow;
                            }}
                            rows={filteredRooms}
                            columns={columns}
                            classes={customDataGridClasses}
                            pageSizeOptions={[500]}
                            getRowId={(row) => row.roomId}
                            hideFooter
                            onRowClick={this.handleRowClick}
                            slots={{
                                columnSortedDescendingIcon: ArrowDropUp,
                                columnSortedAscendingIcon: ArrowDropDown,
                            }}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'seats', sort: 'desc' }]
                                }
                            }}
                            autoHeight={false}
                            disableVirtualization={false}
                            sx={{
                                border: 'none',
                                color: 'white',
                                height: '100%',
                                cursor: this.state.isDragging ? 'grabbing' : 'grab',
                                '& .MuiDataGrid-virtualScroller': {
                                    '&::-webkit-scrollbar': {
                                        width: '0.4em',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(255,255,255,.3)',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'rgba(255,255,255,.5)',
                                    },
                                },
                                '& .MuiDataGrid-main': {
                                    gap: '0.25rem',
                                    width: '100%'
                                },
                                '& .MuiDataGrid-virtualScrollerRenderZone': {
                                    gap: '0.25rem',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    paddingBottom: '1rem'
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    width: '100%'
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    width: '100%',
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-root': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell:first-of-type': {
                                    borderLeft: '2px solid #F52740 !important',
                                },
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                    background: 'linear-gradient(90deg, rgba(245, 39, 64, 0.10) 0%, rgba(28, 28, 37, 0.10) 22.39%), #2A2A32',
                                    transition: 'background 300ms ease',
                                    width: '100%'
                                },
                                '& .MuiDataGrid-row:hover': {
                                    cursor: 'pointer',
                                    background: '#40414C !important',
                                },
                                '& .MuiDataGrid-row:focus': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-row:active': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-sortIcon': {
                                    color: '#66666b',
                                    fontSize: '2rem'
                                },
                                '& .MuiDataGrid-row.Mui-selected': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none !important',
                                },
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '256px', height: '100%', flexGrow: 0, flexShrink: 0, paddingBottom: '1rem', boxSizing: 'border-box' }}>
                        <RoomInformation
                            selectedRoom={this.state.selectedRoom}
                            joinRoom={this.props.joinRoom}
                            userState={this.props.userState}
                        />
                    </div>
                </div>
            </ThemeProvider>
        );
    }
} 
